import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMap } from '../contexts/MapContext';
import { useCampaign } from '../contexts/CampaignContext';
import MapCanvas from '../components/canvas/MapCanvas';
import InitiativeTracker from '../components/initiative/InitiativeTracker';
import SpawnPanel, { PlayersSection } from '../components/dm/SpawnPanel';
import ActiveTokensPanel from '../components/dm/ActiveTokensPanel';
import EditorToolbar from '../components/editor/EditorToolbar';
import MapLibrary from '../components/editor/MapLibrary';
import AiMapImport from '../components/editor/AiMapImport';
import EncounterLibrary from '../components/editor/EncounterLibrary';
import DoorPanel from '../components/dm/DoorPanel';
import PlayerPresence from '../components/dm/PlayerPresence';
import PerceptionPromptPanel from '../components/dm/PerceptionPromptPanel';
import TrapPanel from '../components/dm/TrapPanel';
import TeleportPanel from '../components/dm/TeleportPanel';

import MusicPanel from '../components/dm/MusicPanel';
import EncounterCalc from '../components/initiative/EncounterCalc';
import ReferencePanel from '../components/reference/ReferencePanel';
import DiceTray from '../components/dice/DiceTray';
import DiceRollOverlay from '../components/dice/DiceRollOverlay';
import RollLog from '../components/dice/RollLog';
import { BUFF_MAP } from '../lib/buffs';
import * as paperdollRenderer from '../lib/paperdollRenderer';
import { equippedItem, weaponMeta } from '../lib/equip';
import InventoryPanel from '../components/player/InventoryPanel';
import GrantItemPanel from '../components/dm/GrantItemPanel';
const SIDEBAR_TABS = ['Combat', 'Active', 'Players', 'Spawn', 'Map', 'Rolls', 'Enc.', 'Music', 'Ref.'];

const EDITOR_LABELS = {
  'paint':        '✏️ Paint',
  'eraser':       '⬜ Eraser',
  'fill':         '🪣 Fill',
  'fog-reveal':   '👁 Reveal Fog',
  'fog-hide':     '🌑 Hide Fog',
  'spawn':        '🏠 Click to set spawn point',
  'wall-pen':     '🖊 Wall Pen — dblclick or close to finish',
  'wall-eraser':  '✂ Click a wall segment to remove it',
  'wall-select':  '↖ Select Wall — Delete to remove',
  'door-pen':     '🚪 Door Pen — click two points to place a door',
  'wall-to-door': '↔ Click a wall segment to convert to door',
  'trap-place':   '🪤 Click a cell to place a trap',
  'teleport-pen': '🌀 Teleport — click endpoint A, then endpoint B',
};

export default function DMView() {
  const { auth, logout }   = useAuth();
  const { setSpawnPoint, clearMap, clearWalls, spawnPoint, mapName }  = useMap();
  const { tokens, players, removeToken, moveToken, updateToken, spawnToken, cloneToken, updatePlayer, createPerceptionPrompt } = useCampaign();

  const waitingCount = Object.values(players).filter((p) => {
    const t = Object.values(tokens).find((tk) => tk.ownerId === p.id && tk.type === 'player');
    return t?.hidden;
  }).length;

  // Track player disconnections — notify DM when a player drops off
  const canvasRef = useRef(null);
  const [disconnectAlerts, setDisconnectAlerts] = useState([]);
  const prevOnline = useRef({});
  useEffect(() => {
    const now = Date.now();
    const STALE = 20000; // 20s without heartbeat = offline
    const playerList = Object.values(players);
    const newAlerts = [];
    const currentOnline = {};
    for (const p of playerList) {
      const wasOnline = prevOnline.current[p.id];
      const isOnline = p.lastSeen && (now - p.lastSeen) < STALE;
      currentOnline[p.id] = isOnline;
      if (wasOnline && !isOnline) {
        newAlerts.push(p.name);
      }
    }
    prevOnline.current = currentOnline;
    if (newAlerts.length > 0) {
      setDisconnectAlerts((prev) => [...prev, ...newAlerts.map((n) => ({ name: n, time: now }))]);
    }
  }, [players]);

  // Auto-dismiss alerts after 8s
  useEffect(() => {
    if (disconnectAlerts.length === 0) return;
    const timer = setTimeout(() => {
      setDisconnectAlerts((prev) => prev.filter((a) => Date.now() - a.time < 8000));
    }, 8000);
    return () => clearTimeout(timer);
  }, [disconnectAlerts]);

  const navigate          = useNavigate();
  const { id }            = useParams();

  const [selectedToken,   setSelectedToken]   = useState(null);
  const [hoveredTokenId,  setHoveredTokenId]  = useState(null);
  const [activeTab,       setActiveTab]       = useState('Combat');
  const [combatSelecting,    setCombatSelecting]    = useState(false);
  const [combatSelectedIds,  setCombatSelectedIds]  = useState({});
  const [encSelecting,       setEncSelecting]       = useState(false);
  const [encSelectedIds,     setEncSelectedIds]     = useState(new Set());
  const [encActiveIds,       setEncActiveIds]       = useState(new Set());
  const [editorMode,      setEditorMode]      = useState(null);
  const [activeTerrain,   setActiveTerrain]   = useState('stone_floor');
  const [activeWallStyle, setActiveWallStyle] = useState('stone');
  const [activeDoorStyle, setActiveDoorStyle] = useState('wood');
  const [activeDoorType,  setActiveDoorType]  = useState('door');
  const [activeElevation, setActiveElevation] = useState(1);
  const [wallCurved,      setWallCurved]      = useState(false);
  const [activePathStyle, setActivePathStyle] = useState('dirt');
  const [pathCurved,      setPathCurved]      = useState(false);
  const [activeTeleportStyle, setActiveTeleportStyle] = useState('portal');
  const [teleportTwoWay,      setTeleportTwoWay]      = useState(true);
  const [teleportHidden,      setTeleportHidden]      = useState(false);
  const [teleportDC,          setTeleportDC]          = useState(12);
  const [selectedDoorId,  setSelectedDoorId]  = useState(null);
  const [confirmNewMap,   setConfirmNewMap]   = useState(false);
  const [showDmDice,      setShowDmDice]      = useState(false);

  const handleNewMap = async () => {
    setConfirmNewMap(false);
    // Remove all non-player tokens
    const nonPlayer = Object.values(tokens).filter((t) => t.type !== 'player');
    await Promise.all(nonPlayer.map((t) => removeToken(t.id)));
    // Clear terrain, walls, doors
    clearMap();
    // Move player tokens to spawn
    const sp = spawnPoint || { x: 1, y: 1 };
    const playerTokens = Object.values(tokens).filter((t) => t.type === 'player');
    for (const t of playerTokens) {
      await moveToken(t.id, sp.x, sp.y);
    }
  };

  // Clipboard for token copy/paste
  const clipboardRef = useRef(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;

      // M — toggle the measure tool from any tab. Hands-free distance check.
      if (!e.ctrlKey && !e.metaKey && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        setEditorMode((m) => (m === 'measure' ? null : 'measure'));
        return;
      }

      // Left/right arrows rotate the selected object token in 90° steps
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (!selectedToken) return;
        const t = tokens[selectedToken];
        if (!t || t.type !== 'object') return;
        e.preventDefault();
        const delta = e.key === 'ArrowRight' ? 90 : -90;
        const next  = (((t.rotation || 0) + delta) % 360 + 360) % 360;
        updateToken(t.id, { rotation: next });
        return;
      }

      // Ctrl+C — remember the source token id; paste reads the live record
      // straight from Firestore so every field follows along (sprite, paperdoll,
      // packKey, sizeCategory, rotation, custom flags, …) without spawnToken's
      // restrictive shape silently dropping anything.
      if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        if (!selectedToken) return;
        const t = tokens[selectedToken];
        if (!t) return;
        e.preventDefault();
        clipboardRef.current = { id: t.id, x: t.x, y: t.y };
        return;
      }

      // Ctrl+V — clone the source token near the original
      if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
        const src = clipboardRef.current;
        if (!src) return;
        e.preventDefault();
        cloneToken(src.id, {
          x: (src.x ?? 0) + 1,
          y: (src.y ?? 0) + 1,
          // Per-instance state that shouldn't carry on a fresh paste.
          conditions: [],
          buffs: [],
          actionStates: [],
          defeated: false,
          hidden: false,
          tempHp: 0,
        });
        return;
      }

      // Delete — remove selected token
      if (e.key === 'Delete') {
        if (!selectedToken) return;
        const t = tokens[selectedToken];
        if (!t || t.type === 'player') return;
        e.preventDefault();
        removeToken(selectedToken);
        setSelectedToken(null);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedToken, tokens, updateToken, cloneToken, removeToken]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'Map') { setEditorMode(null); setSelectedDoorId(null); }
    else if (!editorMode) setEditorMode('paint');
    if (tab !== 'Enc.') setEncSelecting(false);
  };

  const handleTokenSelect = useCallback((tokenId) => {
    setSelectedToken(tokenId);
    if (combatSelecting && tokenId) {
      const t = tokens[tokenId];
      if (t && !t.defeated && t.type !== 'object') {
        setCombatSelectedIds((prev) => ({ ...prev, [tokenId]: !prev[tokenId] }));
      }
    } else if (encSelecting && tokenId) {
      const t = tokens[tokenId];
      if (t && t.type !== 'player' && t.type !== 'object') {
        setEncSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(tokenId)) next.delete(tokenId); else next.add(tokenId);
          return next;
        });
      }
    } else if (tokenId) {
      setActiveTab('Active');
    }
  }, [combatSelecting, encSelecting, tokens]);

  const handleDoorClick = useCallback((doorId) => {
    setSelectedDoorId(doorId);
    setEditorMode(null);
    setActiveTab('Map');
  }, []);

  const handleToolChange = (tool) => {
    setEditorMode(tool);
    setSelectedDoorId(null);
  };

  const handleLogout = () => { logout(); navigate(`/session/${id}/dm/login`); };
  const isFogMode  = editorMode === 'fog-reveal' || editorMode === 'fog-hide';
  const isWallMode = editorMode === 'wall-pen'   || editorMode === 'wall-select' || editorMode === 'wall-eraser';
  const isDoorMode = editorMode === 'door-pen'   || editorMode === 'wall-to-door';

  // Online player count for top bar
  const onlineCount = Object.values(players).filter((p) => p.lastSeen && (Date.now() - p.lastSeen) < 20000).length;

  return (
    <div style={styles.layout}>
      {/* Disconnect alerts */}
      {disconnectAlerts.length > 0 && (
        <div style={styles.alertStack}>
          {disconnectAlerts.map((a, i) => (
            <div key={`${a.name}-${a.time}-${i}`} style={styles.disconnectAlert}>
              <span style={styles.alertIcon}>⚠</span>
              <span>{a.name} disconnected</span>
              <button style={styles.alertDismiss} onClick={() => setDisconnectAlerts((prev) => prev.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Left sidebar (party at a glance) ─────────────────────── */}
      <div style={styles.leftSidebar}>
        <div style={styles.leftSidebarHeader}>Party</div>
        <PassiveStatsBar
          players={players}
          tokens={tokens}
          updatePlayer={updatePlayer}
          onSelectToken={handleTokenSelect}
          vertical
        />
      </div>

      {/* ── Map ──────────────────────────────────────────────────── */}
      <div style={styles.mapArea}>
        <MapCanvas
          ref={canvasRef}
          isDm
          onTokenSelect={handleTokenSelect}
          selectedTokenId={selectedToken}
          hoveredTokenId={hoveredTokenId}
          editorMode={editorMode}
          activeTerrain={activeTerrain}
          activeWallStyle={activeWallStyle}
          activeDoorStyle={activeDoorStyle}
          activeDoorType={activeDoorType}
          onDoorClick={handleDoorClick}
          selectedDoorId={selectedDoorId}
          onSpawnSet={(x, y) => { setSpawnPoint(x, y); setEditorMode(null); }}
          activeElevation={activeElevation}
          wallCurved={wallCurved}
          activePathStyle={activePathStyle}
          pathCurved={pathCurved}
          activeTeleportStyle={activeTeleportStyle}
          teleportTwoWay={teleportTwoWay}
          teleportHidden={teleportHidden}
          teleportDC={teleportDC}
          highlightTokenIds={activeTab === 'Enc.' ? encActiveIds : null}
        />

        <div style={styles.topBar}>
          <span style={styles.dmBadge}>⚔ DM</span>
          <span style={styles.mapNameLabel}>{mapName || 'Untitled Map'}</span>
          <PlayerPresence compact />
          <span style={{ flex: 1 }} />
          {editorMode && (
            <span style={isFogMode ? styles.fogBadge : isWallMode ? styles.wallBadge : styles.editorBadge}>
              {EDITOR_LABELS[editorMode]}
            </span>
          )}
          {selectedDoorId && !editorMode && (
            <span style={styles.doorBadge}>🚪 Door selected</span>
          )}
          <div style={styles.topBarActions}>
            <button className="btn-ghost btn-sm" onClick={() => setShowDmDice((v) => !v)} title="Dice tray">🎲</button>
            <button className="btn-ghost btn-sm" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </div>

      {/* ── Sidebar ───────────────────────────────────────────────── */}
      <div style={styles.sidebar}>
        {waitingCount > 0 && (
          <button style={styles.lobbyBanner} onClick={() => handleTabChange('Players')}>
            🔔 {waitingCount} player{waitingCount > 1 ? 's' : ''} waiting in lobby — click to accept
          </button>
        )}
        <div style={styles.tabBar}>
          {SIDEBAR_TABS.map((tab) => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.activeTab : {}) }}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
              {tab === 'Players' && waitingCount > 0 && (
                <span style={styles.tabBadge}>{waitingCount}</span>
              )}
            </button>
          ))}
        </div>

        <div style={styles.tabContent}>
          {activeTab === 'Combat' && <InitiativeTracker isDm selectedTokenId={selectedToken} onSelectToken={handleTokenSelect} onHoverToken={setHoveredTokenId} combatSelecting={combatSelecting} setCombatSelecting={setCombatSelecting} combatSelectedIds={combatSelectedIds} setCombatSelectedIds={setCombatSelectedIds} />}
          {activeTab === 'Active' && <ActiveTokensPanel selectedTokenId={selectedToken} onClearToken={() => setSelectedToken(null)} onSelectToken={handleTokenSelect} />}
          {activeTab === 'Players' && <PlayersSection getViewCenter={() => canvasRef.current?.getViewCenter?.()} />}
          {activeTab === 'Spawn' && <SpawnPanel getViewCenter={() => canvasRef.current?.getViewCenter?.()} />}
          {activeTab === 'Map'    && (
            <div style={styles.mapEditorPanel}>
              <EditorToolbar
                activeTool={editorMode}
                onToolChange={handleToolChange}
                activeWallStyle={activeWallStyle}
                onWallStyleChange={setActiveWallStyle}
                activeDoorStyle={activeDoorStyle}
                onDoorStyleChange={setActiveDoorStyle}
                activeDoorType={activeDoorType}
                onDoorTypeChange={setActiveDoorType}
                activeTerrain={activeTerrain}
                onTerrainSelect={(t) => { setActiveTerrain(t); setEditorMode('paint'); }}
                activeElevation={activeElevation}
                onElevationChange={setActiveElevation}
                wallCurved={wallCurved}
                onWallCurvedChange={setWallCurved}
                activePathStyle={activePathStyle}
                onPathStyleChange={setActivePathStyle}
                pathCurved={pathCurved}
                onPathCurvedChange={setPathCurved}
                activeTeleportStyle={activeTeleportStyle}
                onTeleportStyleChange={setActiveTeleportStyle}
                teleportTwoWay={teleportTwoWay}
                onTeleportTwoWayChange={setTeleportTwoWay}
                teleportHidden={teleportHidden}
                onTeleportHiddenChange={setTeleportHidden}
                teleportDC={teleportDC}
                onTeleportDCChange={setTeleportDC}
              />
              {selectedDoorId && !editorMode && (
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #333355', flexShrink: 0 }}>
                  <DoorPanel
                    doorId={selectedDoorId}
                    onClose={() => setSelectedDoorId(null)}
                  />
                </div>
              )}
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #333355', flexShrink: 0 }}>
                <PerceptionPromptPanel />
              </div>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #333355', flexShrink: 0 }}>
                <TrapPanel />
              </div>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #333355', flexShrink: 0 }}>
                <TeleportPanel />
              </div>
              <MapLibrary />
              <div style={styles.newMapSection}>
                {confirmNewMap ? (
                  <div style={styles.newMapConfirm}>
                    <span style={styles.newMapWarn}>Clear all terrain, walls, and non-player tokens?</span>
                    <div style={styles.newMapBtns}>
                      <button style={styles.newMapYes} onClick={handleNewMap}>Yes, new map</button>
                      <button style={styles.newMapNo} onClick={() => setConfirmNewMap(false)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button style={styles.newMapBtn} onClick={() => setConfirmNewMap(true)}>
                    📄 New Map
                  </button>
                )}
              </div>
              <AiMapImport />
              <EncounterLibrary />
            </div>
          )}
          {activeTab === 'Enc.' && (
            <div style={{ height: '100%', overflowY: 'auto' }}>
              <EncounterCalc
                onHoverToken={setHoveredTokenId}
                encSelecting={encSelecting}
                setEncSelecting={setEncSelecting}
                encSelectedIds={encSelectedIds}
                setEncSelectedIds={setEncSelectedIds}
                onActiveMonsterIds={setEncActiveIds}
              />
            </div>
          )}
          {activeTab === 'Music' && <MusicPanel />}
          {activeTab === 'Ref.'  && <ReferencePanel />}
          {activeTab === 'Rolls' && (
            <div style={{ padding: 8 }}>
              <RollLog maxHeight={'calc(100vh - 200px)'} viewerIdentity={{ isDm: true }} />
            </div>
          )}
        </div>
      </div>

      {/* DM dice tray — modal-ish slide up from bottom centre */}
      {showDmDice && (
        <>
          <div style={styles.dmDiceBackdrop} onClick={() => setShowDmDice(false)} />
          <div style={styles.dmDiceWrap}>
            <DiceTray
              identity={{ playerId: null, playerName: 'DM', colour: '#d4a847', isDm: true }}
              onClose={() => setShowDmDice(false)}
            />
          </div>
        </>
      )}

      <DiceRollOverlay viewerIdentity={{ playerId: null, isDm: true }} />
    </div>
  );
}

function PassiveStatsBar({ players, tokens, updatePlayer, onSelectToken, vertical = false }) {
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [inventoryPlayerId, setInventoryPlayerId] = useState(null);
  const [grantPlayerId, setGrantPlayerId] = useState(null);
  const list = Object.values(players || {}).filter((p) => p.charName);

  // Esc closes the expanded card. Hook order is fixed before any early return.
  useEffect(() => {
    if (!expandedPlayerId) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpandedPlayerId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedPlayerId]);

  if (list.length === 0) return null;

  const longRest = async () => {
    if (!confirm('Long Rest — reset all spell slots for the party?')) return;
    for (const p of list) {
      const slots = p.spellSlots || {};
      const reset = {};
      for (const [lvl, slot] of Object.entries(slots)) {
        reset[lvl] = { ...slot, used: 0 };
      }
      await updatePlayer(p.id, { spellSlots: reset });
    }
  };

  // Map player.id → their player-type token (for live HP / conditions / buffs)
  const tokenByPlayerId = (() => {
    const map = {};
    for (const t of Object.values(tokens || {})) {
      if (t.type === 'player' && t.ownerId) map[t.ownerId] = t;
    }
    return map;
  })();

  const toggleExpand = (id) => setExpandedPlayerId((cur) => (cur === id ? null : id));

  return (
    <>
      {expandedPlayerId && (
        <div style={passiveStyles.backdrop} onClick={() => setExpandedPlayerId(null)} />
      )}
      <div style={vertical ? passiveStyles.barVertical : passiveStyles.bar}>
        <button style={passiveStyles.restBtn} onClick={longRest} title="Reset all party spell slots">
          🌙 Long Rest
        </button>
        {list.map((p) => {
          const slotEntries = Object.entries(p.spellSlots || {})
            .filter(([, s]) => (s?.max || 0) > 0)
            .sort((a, b) => Number(a[0]) - Number(b[0]));
          const expanded = expandedPlayerId === p.id;
          const tok = tokenByPlayerId[p.id];
          const conditions = tok?.conditions || [];
          const buffs      = (tok?.buffs || []).filter((b) => b.id !== 'mirror_image');

          // Collapsed-card vs expanded-card top region. Collapsed: simple
          // dot + name. Expanded: paperdoll on the left, name/passive stats
          // stacked on the right.
          const headerNode = expanded ? (
            <div style={passiveStyles.expandHeader}>
              {p.paperdoll && <PaperdollPortrait paperdoll={p.paperdoll} size={72} />}
              <div style={passiveStyles.expandHeaderRight}>
                <div style={passiveStyles.nameRow}>
                  <span style={{ ...passiveStyles.dot, background: p.colour || '#c084fc' }} />
                  <span style={passiveStyles.name}>{p.charName}</span>
                  <button
                    style={passiveStyles.collapseBtn}
                    onClick={(e) => { e.stopPropagation(); setExpandedPlayerId(null); }}
                    title="Collapse"
                  >✕</button>
                </div>
                <div style={passiveStyles.stats}>
                  <span style={passiveStyles.stat} title="Passive Perception">
                    <span style={passiveStyles.statLabel}>PER</span>
                    <span style={passiveStyles.statValue}>{p.passivePerception ?? '—'}</span>
                  </span>
                  <span style={passiveStyles.stat} title="Passive Investigation">
                    <span style={passiveStyles.statLabel}>INV</span>
                    <span style={passiveStyles.statValue}>{p.passiveInvestigation ?? '—'}</span>
                  </span>
                  <span style={passiveStyles.stat} title="Passive Insight">
                    <span style={passiveStyles.statLabel}>INS</span>
                    <span style={passiveStyles.statValue}>{p.passiveInsight ?? '—'}</span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={passiveStyles.nameRow}>
                <span style={{ ...passiveStyles.dot, background: p.colour || '#c084fc' }} />
                <span style={passiveStyles.name}>{p.charName}</span>
              </div>
              <div style={passiveStyles.stats}>
                <span style={passiveStyles.stat} title="Passive Perception">
                  <span style={passiveStyles.statLabel}>PER</span>
                  <span style={passiveStyles.statValue}>{p.passivePerception ?? '—'}</span>
                </span>
                <span style={passiveStyles.stat} title="Passive Investigation">
                  <span style={passiveStyles.statLabel}>INV</span>
                  <span style={passiveStyles.statValue}>{p.passiveInvestigation ?? '—'}</span>
                </span>
                <span style={passiveStyles.stat} title="Passive Insight">
                  <span style={passiveStyles.statLabel}>INS</span>
                  <span style={passiveStyles.statValue}>{p.passiveInsight ?? '—'}</span>
                </span>
              </div>
            </>
          );

          return (
            <div
              key={p.id}
              style={{
                ...passiveStyles.card,
                ...(expanded ? passiveStyles.cardExpanded : {}),
                cursor: expanded ? 'default' : 'pointer',
              }}
              // Tap-to-expand only when collapsed. When expanded, the X
              // button and backdrop are the only ways to dismiss — clicks
              // inside the expanded body (action buttons, etc.) just act
              // without bubbling the card into a toggle.
              onClick={expanded ? undefined : () => toggleExpand(p.id)}
            >
              {headerNode}
              {slotEntries.length > 0 && (
                <div style={passiveStyles.slots}>
                  {slotEntries.map(([lvl, slot]) => {
                    const remaining = slot.max - (slot.used || 0);
                    const depleted  = remaining === 0;
                    return (
                      <span
                        key={lvl}
                        style={{ ...passiveStyles.slot, ...(depleted ? passiveStyles.slotDepleted : {}) }}
                        title={`Level ${lvl}: ${remaining}/${slot.max} remaining`}
                      >
                        <span style={passiveStyles.slotLvl}>L{lvl}</span>
                        <span style={passiveStyles.slotCount}>{remaining}/{slot.max}</span>
                      </span>
                    );
                  })}
                </div>
              )}

              {expanded && (
                <div style={passiveStyles.expandBody} onClick={(e) => e.stopPropagation()}>
                  <div style={passiveStyles.expandRow}>
                    <span style={passiveStyles.expandLabel}>HP</span>
                    <span style={passiveStyles.expandValueStrong}>
                      {tok ? `${tok.hp}/${tok.maxHp}` : '—'}
                    </span>
                    {(tok?.tempHp || 0) > 0 && (
                      <span style={passiveStyles.tempBadge}>+{tok.tempHp} temp</span>
                    )}
                  </div>
                  <div style={passiveStyles.expandMetaRow}>
                    {p.class && (
                      <span style={passiveStyles.metaPill}>{p.class} L{p.level || 1}</span>
                    )}
                    <span style={passiveStyles.metaPill}>AC {p.ac ?? '—'}</span>
                    <span style={passiveStyles.metaPill}>{p.speed ?? 30}ft</span>
                    <span style={passiveStyles.metaPill}>
                      Init {(p.initiativeBonus ?? 0) >= 0 ? '+' : ''}{p.initiativeBonus ?? 0}
                    </span>
                    <span style={passiveStyles.metaPill}>Vis {p.visionRange ?? 60}ft</span>
                  </div>

                  {/* Ability scores grid */}
                  {p.abilityScores && (
                    <div style={passiveStyles.abilityGrid}>
                      {['str', 'dex', 'con', 'int', 'wis', 'cha'].map((k) => {
                        const score = p.abilityScores[k] ?? 10;
                        const mod   = Math.floor((score - 10) / 2);
                        return (
                          <div key={k} style={passiveStyles.abilityCell}>
                            <span style={passiveStyles.abilityLabel}>{k.toUpperCase()}</span>
                            <span style={passiveStyles.abilityScore}>{score}</span>
                            <span style={passiveStyles.abilityMod}>
                              {mod >= 0 ? `+${mod}` : mod}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Equipped slots — armor / main hand / off hand */}
                  {(() => {
                    const inv  = p.inventory || [];
                    const armor    = equippedItem(inv, 'armor');
                    const mainHand = equippedItem(inv, 'mainHand');
                    const offHand  = equippedItem(inv, 'offHand');
                    if (!armor && !mainHand && !offHand) return null;
                    return (
                      <div style={passiveStyles.expandPillsRow}>
                        <span style={passiveStyles.pillsLabel}>Equipped</span>
                        <div style={passiveStyles.equippedList}>
                          <EquippedRow icon="🛡" slot="Armor" item={armor} />
                          <EquippedRow icon="⚔" slot="Main"  item={mainHand} />
                          <EquippedRow icon="✋" slot="Off"   item={offHand} />
                        </div>
                      </div>
                    );
                  })()}

                  {conditions.length > 0 && (
                    <div style={passiveStyles.expandPillsRow}>
                      <span style={passiveStyles.pillsLabel}>Conditions</span>
                      <div style={passiveStyles.pills}>
                        {conditions.map((c) => (
                          <span key={c} style={passiveStyles.condPill}>{c}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {buffs.length > 0 && (
                    <div style={passiveStyles.expandPillsRow}>
                      <span style={passiveStyles.pillsLabel}>Buffs</span>
                      <div style={passiveStyles.pills}>
                        {buffs.map((b, i) => {
                          const def    = BUFF_MAP[b.id];
                          const colour = b.color || def?.color || '#c084fc';
                          const icon   = b.icon  || def?.icon  || '✨';
                          return (
                            <span
                              key={i}
                              style={{ ...passiveStyles.buffPill, borderColor: colour, color: colour }}
                            >
                              {icon} {b.label || def?.label || b.id}
                              {!b.permanent && b.duration != null && (
                                <span style={passiveStyles.buffDur}>⏳ {b.duration}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* DM quick-actions for this player */}
                  <div style={passiveStyles.actionGrid}>
                    <button
                      style={{
                        ...passiveStyles.actionBtn,
                        ...(p.inspiration ? passiveStyles.actionBtnInspired : {}),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        updatePlayer(p.id, { inspiration: !p.inspiration });
                      }}
                      title={p.inspiration ? 'Revoke Inspiration' : 'Grant Inspiration'}
                    >
                      ✨ {p.inspiration ? 'Revoke' : 'Inspire'}
                    </button>
                    <button
                      style={passiveStyles.actionBtn}
                      onClick={(e) => { e.stopPropagation(); setInventoryPlayerId(p.id); }}
                      title="Open inventory & wallet"
                    >
                      🎒 Inventory
                    </button>
                    <button
                      style={passiveStyles.actionBtn}
                      onClick={(e) => { e.stopPropagation(); setGrantPlayerId(p.id); }}
                      title="Grant an item from the catalog (free)"
                    >
                      🎁 Grant
                    </button>
                    {tok && onSelectToken && (
                      <button
                        style={passiveStyles.actionBtn}
                        onClick={(e) => { e.stopPropagation(); onSelectToken(tok.id); setExpandedPlayerId(null); }}
                      >
                        🎯 Inspect token
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Inventory modal — overlays the DM view when an Inventory button is tapped */}
      {inventoryPlayerId && players?.[inventoryPlayerId] && (
        <InventoryPanel
          inventory={players[inventoryPlayerId].inventory || []}
          currency={players[inventoryPlayerId].currency || {}}
          onInventoryChange={(next) => updatePlayer(inventoryPlayerId, { inventory: next })}
          onCurrencyChange={(next)  => updatePlayer(inventoryPlayerId, { currency: next })}
          editable
          title={`🎒 ${players[inventoryPlayerId].charName || 'Player'} — Inventory`}
          onClose={() => setInventoryPlayerId(null)}
        />
      )}

      {/* Grant-item modal — DM gives any catalog item (relics pinned on top) */}
      {grantPlayerId && (
        <GrantItemPanel
          initialPlayerId={grantPlayerId}
          onClose={() => setGrantPlayerId(null)}
        />
      )}
    </>
  );
}

// Small static portrait — draws the player's paperdoll (Idl/down/frame 0)
// into a 1:1 canvas. Re-renders whenever paperdollRenderer reports atlases
// finished loading or whenever the paperdoll config changes.
function PaperdollPortrait({ paperdoll, size = 72 }) {
  const ref = useRef(null);
  const [tick, setTick] = useState(0);
  useEffect(() => paperdollRenderer.subscribe(() => setTick((t) => t + 1)), []);
  useEffect(() => {
    if (!ref.current) return;
    // The renderer returns a canvas at `size*1.4` so the sprite has headroom;
    // we scale-fit it down by drawing into our smaller canvas.
    const src = paperdollRenderer.getPaperdollFrame(paperdoll, 'Idl', 'down', 0, Math.round(size * 1.4));
    const ctx = ref.current.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, size, size);
    if (src) {
      // Anchor feet to the bottom, like TokenLayer does.
      const offsetY = Math.round(size * 0.12);
      ctx.drawImage(src, 0, 0, src.width, src.height, -size * 0.2, offsetY - size * 0.1, size * 1.4, size * 1.4);
    }
  }, [paperdoll, size, tick]);
  return (
    <canvas
      ref={ref}
      width={size}
      height={size}
      style={{ width: size, height: size, background: '#0a0f1e', border: '1px solid #4a3d28', borderRadius: 4, flexShrink: 0 }}
    />
  );
}

// One row inside the Equipped section of the expanded party card.
function EquippedRow({ icon, slot, item }) {
  const meta = item && weaponMeta(item);
  return (
    <div style={passiveStyles.equippedRow}>
      <span style={passiveStyles.equippedSlot}>{icon} {slot}</span>
      {item ? (
        <span style={passiveStyles.equippedName} title={item.description || item.name}>
          {item.name}
          {meta?.damageDie && <span style={passiveStyles.equippedMeta}> · {meta.damageDie}</span>}
        </span>
      ) : (
        <span style={passiveStyles.equippedEmpty}>—</span>
      )}
    </div>
  );
}

const passiveStyles = {
  backdrop:   { position: 'absolute', inset: 0, zIndex: 4, background: 'transparent' },
  bar:        { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(8px)', borderTop: '1px solid #4a3d28', padding: '6px 10px', display: 'flex', alignItems: 'flex-end', gap: 8, overflowX: 'auto', overflowY: 'visible', zIndex: 5, pointerEvents: 'auto' },
  barVertical: { display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', overflowY: 'auto', position: 'relative', zIndex: 5 },
  expandHeader:      { display: 'flex', gap: 8, alignItems: 'stretch' },
  expandHeaderRight: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  abilityGrid:       { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 3, marginTop: 4 },
  abilityCell:       { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3px 2px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, minWidth: 0 },
  abilityLabel:      { fontSize: 8, fontWeight: 700, color: '#9a8b6e', letterSpacing: '0.04em' },
  abilityScore:      { fontSize: 13, fontWeight: 800, color: '#f0e6d0', lineHeight: 1.1 },
  abilityMod:        { fontSize: 9, fontWeight: 700, color: '#d4a847' },
  equippedList:      { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  equippedRow:       { display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 10 },
  equippedSlot:      { color: '#9a8b6e', fontWeight: 700, minWidth: 50 },
  equippedName:      { color: '#f0e6d0', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  equippedMeta:      { color: '#7ab8e0', fontWeight: 600 },
  equippedEmpty:     { color: '#4a3d28', fontStyle: 'italic' },
  card:       { background: '#142038', border: '1px solid #4a3d28', borderRadius: 4, padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120, flexShrink: 0, cursor: 'pointer', transition: 'border-color 0.15s' },
  cardExpanded:{ borderColor: '#d4a847', background: '#1a2a48', minWidth: 240, boxShadow: '0 -8px 24px rgba(0,0,0,0.5)' },
  collapseBtn: { marginLeft: 'auto', background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1 },
  expandBody:  { marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(74,61,40,0.5)', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'default' },
  expandRow:   { display: 'flex', alignItems: 'baseline', gap: 6 },
  expandLabel: { fontSize: 9, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  expandValueStrong: { fontSize: 14, fontWeight: 800, color: '#d4a847' },
  tempBadge:   { fontSize: 10, color: '#7ab8e0', fontWeight: 700 },
  expandMetaRow: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  metaPill:    { fontSize: 10, color: '#f0e6d0', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, padding: '1px 6px', fontWeight: 600 },
  expandPillsRow: { display: 'flex', flexDirection: 'column', gap: 3 },
  pillsLabel:  { fontSize: 9, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  pills:       { display: 'flex', flexWrap: 'wrap', gap: 3 },
  condPill:    { fontSize: 10, background: 'rgba(192,57,43,0.18)', border: '1px solid rgba(192,57,43,0.55)', color: '#e07070', borderRadius: 3, padding: '1px 6px', textTransform: 'capitalize' },
  buffPill:    { fontSize: 10, background: 'rgba(212,168,71,0.08)', border: '1px solid', borderRadius: 3, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 4 },
  buffDur:     { fontSize: 9, fontWeight: 700, background: 'rgba(0,0,0,0.35)', borderRadius: 8, padding: '0 5px', color: 'inherit' },
  inspectLink: { marginTop: 4, alignSelf: 'flex-start', background: 'transparent', border: '1px solid #4a3d28', borderRadius: 3, color: '#d4a847', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", padding: '4px 8px', cursor: 'pointer', letterSpacing: '0.04em' },
  actionGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginTop: 6 },
  actionBtn:  { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 3, color: '#d4a847', fontSize: 9, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", padding: '5px 4px', cursor: 'pointer', letterSpacing: '0.03em', textAlign: 'center' },
  actionBtnInspired: { borderColor: '#fbbf24', color: '#fde68a', background: 'rgba(251,191,36,0.12)', boxShadow: '0 0 8px rgba(251,191,36,0.3)' },
  nameRow:    { display: 'flex', alignItems: 'center', gap: 5 },
  dot:        { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  name:       { fontSize: 12, fontWeight: 700, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  stats:      { display: 'flex', gap: 10 },
  stat:       { display: 'flex', alignItems: 'baseline', gap: 3 },
  statLabel:  { fontSize: 9, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.04em' },
  statValue:  { fontSize: 13, color: '#d4a847', fontWeight: 800 },
  restBtn:    { background: 'rgba(122,87,184,0.15)', border: '1px solid rgba(122,87,184,0.5)', color: '#c9b3e8', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', flexShrink: 0, alignSelf: 'center', letterSpacing: '0.04em' },
  slots:      { display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' },
  slot:       { display: 'inline-flex', alignItems: 'baseline', gap: 3, fontSize: 10, background: 'rgba(122,87,184,0.12)', border: '1px solid rgba(122,87,184,0.3)', borderRadius: 3, padding: '1px 5px' },
  slotDepleted:{ background: 'rgba(74,61,40,0.4)', border: '1px solid rgba(74,61,40,0.5)', opacity: 0.55 },
  slotLvl:    { fontSize: 9, color: '#a987d4', fontWeight: 700 },
  slotCount:  { fontSize: 11, color: '#c9b3e8', fontWeight: 800 },
};

const styles = {
  layout:        { display: 'flex', width: '100%', height: '100%', overflow: 'hidden' },
  mapArea:       { flex: 1, position: 'relative', overflow: 'hidden' },
  dmDiceBackdrop:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200 },
  dmDiceWrap:    { position: 'fixed', left: '50%', bottom: 0, transform: 'translateX(-50%)', zIndex: 201, width: '100%', display: 'flex', justifyContent: 'center', animation: 'toastSlideIn 220ms ease-out' },
  sidebar:       { width: 280, flexShrink: 0, background: 'linear-gradient(180deg, #1a2a48 0%, #142038 100%)', borderLeft: '2px solid #8b7340', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  leftSidebar:   { width: 260, flexShrink: 0, background: 'linear-gradient(180deg, #1a2a48 0%, #142038 100%)', borderRight: '2px solid #8b7340', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  leftSidebarHeader: { padding: '10px 14px 8px', fontSize: 11, color: '#d4a847', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #4a3d28', flexShrink: 0 },
  tabBar:        { display: 'flex', flexWrap: 'wrap', borderBottom: '2px solid #8b7340', flexShrink: 0 },
  tab:           { width: '33.3333%', background: 'transparent', border: 'none', borderRadius: 0, padding: '10px 0', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", color: '#9a8b6e', cursor: 'pointer', letterSpacing: '0.04em', boxSizing: 'border-box' },
  activeTab:     { color: '#d4a847', borderBottom: '2px solid #d4a847' },
  tabContent:    { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  mapEditorPanel:{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' },
  paletteDimmed: { opacity: 0.35, pointerEvents: 'none' },
  newMapSection: { padding: '10px 14px', borderTop: '1px solid #4a3d28' },
  newMapBtn:     { width: '100%', padding: '10px 0', background: '#2a1a1a', border: '1px solid #8b4040', borderRadius: 6, color: '#e07070', fontSize: 13, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em' },
  newMapConfirm: { display: 'flex', flexDirection: 'column', gap: 8 },
  newMapWarn:    { fontSize: 12, color: '#e07070', fontWeight: 600 },
  newMapBtns:    { display: 'flex', gap: 8 },
  newMapYes:     { flex: 1, padding: '8px 0', background: '#8b4040', border: 'none', borderRadius: 5, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  newMapNo:      { flex: 1, padding: '8px 0', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 5, color: '#9a8b6e', fontSize: 12, cursor: 'pointer' },
  topBar:        { position: 'absolute', top: 0, left: 0, right: 0, height: 44, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', background: 'rgba(10,15,30,0.9)', backdropFilter: 'blur(8px)', borderBottom: '2px solid #8b7340', zIndex: 5 },
  dmBadge:       { background: 'linear-gradient(180deg, #d4a847, #a68330)', color: '#1a0e00', fontSize: 11, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", padding: '3px 10px', borderRadius: 3, letterSpacing: '0.06em', border: '1px solid #c4a44a' },
  mapNameLabel:  { fontSize: 13, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", color: '#f0e6d0', letterSpacing: '0.04em' },
  campaignLabel: { flex: 1, fontSize: 13, color: '#9a8b6e', fontFamily: "'Press Start 2P', monospace" },
  editorBadge:   { fontSize: 11, fontFamily: "'Press Start 2P', monospace", color: '#d4a847', background: 'rgba(212,168,71,0.15)', padding: '3px 10px', borderRadius: 3, border: '1px solid #4a3d28' },
  fogBadge:      { fontSize: 11, fontFamily: "'Press Start 2P', monospace", color: '#7ab8e0', background: 'rgba(122,184,224,0.12)', padding: '3px 10px', borderRadius: 3, border: '1px solid #2a4a60' },
  wallBadge:     { fontSize: 11, fontFamily: "'Press Start 2P', monospace", color: '#d4943a', background: 'rgba(212,148,58,0.12)', padding: '3px 10px', borderRadius: 3, border: '1px solid #5a3a18' },
  doorBadge:     { fontSize: 11, fontFamily: "'Press Start 2P', monospace", color: '#d4943a', background: 'rgba(212,148,58,0.12)', padding: '3px 10px', borderRadius: 3, border: '1px solid #5a3a18' },
  topBarActions: { display: 'flex', gap: 8, alignItems: 'center' },
  lobbyBanner:   { width: '100%', background: 'rgba(212,148,58,0.12)', border: '1px solid rgba(212,148,58,0.4)', color: '#d4943a', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", padding: '8px 14px', cursor: 'pointer', textAlign: 'left', flexShrink: 0 },
  tabBadge:      { background: '#d4943a', color: '#1a0e00', fontSize: 10, fontWeight: 800, padding: '1px 5px', borderRadius: 3, marginLeft: 4 },
  onlineBadge:   { fontSize: 11, color: '#5cb870', background: 'rgba(74,154,90,0.15)', padding: '2px 8px', borderRadius: 3, fontWeight: 700, border: '1px solid rgba(74,154,90,0.3)' },
  alertStack:    { position: 'absolute', top: 52, left: '50%', transform: 'translateX(-50%)', zIndex: 30, display: 'flex', flexDirection: 'column', gap: 6, pointerEvents: 'auto' },
  disconnectAlert: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(192,57,43,0.2)', border: '2px solid rgba(192,57,43,0.5)', backdropFilter: 'blur(8px)', color: '#e07070', fontSize: 13, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", padding: '8px 14px', borderRadius: 4, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.5)' },
  alertIcon:     { fontSize: 16 },
  alertDismiss:  { background: 'transparent', border: 'none', color: '#e0707088', fontSize: 14, cursor: 'pointer', padding: '0 2px', marginLeft: 4 },
};
